import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React__default, { PureComponent, createRef } from 'react';
import PT from 'prop-types';
import styled from 'styled-components';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-AmBipDtf-AmBipDtf.js';
import { g as getFocusableElements } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFocusableElements-BjFtn5aB-BjFtn5aB.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

const StyledButtonGroup = /*#__PURE__*/styled('fieldset').withConfig({
  componentId: "sc-storm-ui-30065889__sc-w0bwkc-0"
})(["border:none;display:block;width:fit-content;margin:0;position:relative;:focus-within,:focus{outline:none;}> button{margin-inline-start:2px;}> button:first-of-type{/*! @noflip */ border-radius:", " 0 0 ", ";border-left-width:1px;::after{/*! @noflip */ border-radius:", " 0 0 ", ";}/*! @noflip */ [dir=\"rtl\"] &{/*! @noflip */ border-radius:0 ", " ", " 0;::after{/*! @noflip */ border-radius:0 ", " ", " 0;}border-left-width:0px;border-right-width:1px;}margin-inline-start:0px;}> button:last-of-type{/*! @noflip */ border-radius:0 ", " ", " 0;::after{/*! @noflip */ border-radius:0 ", " ", " 0;}/*! @noflip */ [dir=\"rtl\"] &{/*! @noflip */ border-radius:", " 0 0 ", ";::after{/*! @noflip */ border-radius:", " 0 0 ", ";}}}margin-inline-start:0;margin-inline-end:0;padding-block-start:0;padding-inline-start:0;padding-inline-end:0;padding-block-end:0;min-inline-size:0;"], ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius, ({
  theme
}) => theme.button.borderRadius);
StyledButtonGroup.displayName = 'StyledButtonGroup';
const StyledLegendSrOnly = /*#__PURE__*/styled.legend.withConfig({
  componentId: "sc-storm-ui-30065889__sc-w0bwkc-1"
})(["", ""], ({
  theme
}) => theme.typography.screenReaderOnly);
StyledLegendSrOnly.displayName = 'StyledLegendSrOnly';
class ButtonGroup extends PureComponent {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "buttonGroupRef", /*#__PURE__*/createRef());
    // KeyDown events to handle for keyboard accessibility. Best practices for Tabs with manual
    // activation here: https://www.w3.org/TR/wai-aria-practices/examples/tabs/tabs-2/tabs.html
    _defineProperty(this, "handleKeyDown", event => {
      const {
        key
      } = event;

      // By default, buttons handle the Enter key on KeyDown. We prevent this because we want
      // to listen to KeyUp to prevent repeat triggers.
      if (key === KEYBOARD_KEYNAMES.ENTER) {
        event.preventDefault();
      }
      if (typeof document !== 'undefined' && this.buttonGroupRef.current) {
        const focusableEls = getFocusableElements(this.buttonGroupRef.current);
        const firstFocusableEl = focusableEls[0];
        const lastFocusableEl = focusableEls[focusableEls.length - 1];

        // When "Left Arrow" or "Right Arrow" is pressed, move focus to the left or right
        if (key === KEYBOARD_KEYNAMES.ARROW_DOWN || key === KEYBOARD_KEYNAMES.ARROW_UP || key === KEYBOARD_KEYNAMES.ARROW_LEFT || key === KEYBOARD_KEYNAMES.ARROW_RIGHT) {
          const currentIndex = focusableEls.indexOf(document.activeElement);
          let newFocusIndex = 0;

          // When "Down Arrow" is pressed, focus to the next
          if (key === KEYBOARD_KEYNAMES.ARROW_DOWN || key === KEYBOARD_KEYNAMES.ARROW_RIGHT) {
            newFocusIndex = currentIndex + 1;
            if (document.activeElement === lastFocusableEl) {
              newFocusIndex = 0;
            }
          }

          // When "Up Arrow" is pressed, focus to the previous
          if (key === KEYBOARD_KEYNAMES.ARROW_UP || key === KEYBOARD_KEYNAMES.ARROW_LEFT) {
            newFocusIndex = currentIndex - 1;
            if (document.activeElement === firstFocusableEl) {
              newFocusIndex = focusableEls.length - 1;
            }
          }
          focusableEls[newFocusIndex].focus();
          event.preventDefault();
        }
      }
    });
    _defineProperty(this, "handleChangeActive", (value, event) => {
      const {
        onChange,
        selectedValue
      } = this.props;
      if (value !== selectedValue && onChange) {
        onChange(value, event);
      }
    });
  }
  renderChildren() {
    const {
      name,
      small,
      selectedValue,
      children
    } = this.props;
    return React__default.Children.map(children, child => {
      if (! /*#__PURE__*/React__default.isValidElement(child)) return null;
      return /*#__PURE__*/React__default.cloneElement(child, {
        key: child.props.value,
        name,
        small,
        isChecked: selectedValue === child.props.value,
        onChange: this.handleChangeActive,
        id: `${name}-${child.props.value}`
      });
    });
  }
  render() {
    const {
      label
    } = this.props;
    return /*#__PURE__*/React__default.createElement(StyledButtonGroup, {
      onKeyDown: event => this.handleKeyDown(event),
      ref: this.buttonGroupRef,
      role: "radiogroup"
    }, label && /*#__PURE__*/React__default.createElement(StyledLegendSrOnly, null, label), this.renderChildren());
  }
}
_defineProperty(ButtonGroup, "propTypes", {
  /**
   * Children of ButtonGroup must be ButtonGroupItems.
   */
  children: PT.node.isRequired,
  /**
   * The text displayed above the ButtonGroup.
   */
  label: PT.string,
  /**
   * This MUST be unique, and it allows the group to manage all children properly.
   */
  name: PT.string.isRequired,
  /**
   * Determines size of button group.
   */
  small: PT.bool,
  /**
   * Called when the user changes the component state.
   */
  onChange: PT.func,
  /**
   * The value returned when the button is clicked.
   */
  selectedValue: PT.string
});
_defineProperty(ButtonGroup, "defaultProps", {
  label: '',
  small: false,
  selectedValue: '',
  onChange: noop
});

export { ButtonGroup as B };
//# sourceMappingURL=ButtonGroup-ChVWlqft.js.map
